import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SwaggerContext from "../../SwaggerContext";
import FeedbackModal from "../../components/FeedbackModal";
import SupportModal from "../../components/SupportModal";
import ScoreDisplay from "../../components/ScoreDisplay";
import { scoreService } from "../../services/scoreService";
import { useGameServices } from "../../hooks/useSharedServices";
import {
  getModeByTitle,
  getSessionType,
  getRandomPoints,
  getSuccessMessage,
  INCORRECT_MESSAGE,
  BUTTON_COLORS,
  extractYouTubeId,
  sortPageQuestions,
} from "behaviour-analyzer-shared";

const Game = () => {
  const { pageApi } = useContext(SwaggerContext);
  const { gameSessionService, userAnswerService } = useGameServices();
  const location = useLocation();
  const { modality, isNewGame } = location.state || {};
  const navigate = useNavigate();

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [score, setScore] = useState(0);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);

  const inFlightRef = useRef(false);
  const completedPagesRef = useRef(new Set());

  const mappedModality = useMemo(
    () => getModeByTitle(modality)?.value,
    [modality]
  );

  // Cargar o crear sesión activa (el backend siempre devuelve una sesión)
  const loadOrCreateSession = useCallback(async () => {
    if (!mappedModality) return null;
    const sessionType = getSessionType(mappedModality);
    const result = await gameSessionService.getOrCreateSession(sessionType);
    if (result.success && result.data) {
      setCurrentSession(result.data);
      return result.data;
    }
    return null;
  }, [mappedModality, gameSessionService]);

  // Marcar página como completada (evitando duplicados)
  const markCurrentPageAsCompleted = useCallback(async () => {
    if (!currentSession || !page) return;

    // Verificar si ya se marcó esta página en esta sesión
    const pageKey = `${currentSession.id}-${page.id}`;
    if (completedPagesRef.current.has(pageKey)) {
      return; // Ya fue marcada, no hacer nada
    }

    // Marcar como completada
    const result = await gameSessionService.markPageAsCompleted(currentSession.id, page.id);
    if (result.success) {
      completedPagesRef.current.add(pageKey);
    }
  }, [currentSession, page, gameSessionService]);

  const fetchPage = useCallback(async () => {
    if (!mappedModality) {
      navigate("/");
      return;
    }

    if (inFlightRef.current) return;
    inFlightRef.current = true;

    setLoading(true);
    setError(null);

    const typesToSend = Array.isArray(mappedModality)
      ? mappedModality
      : [mappedModality];

    // Asegurarse de que tenemos una sesión activa
    let sessionId = currentSession?.id;
    if (!sessionId) {
      // Obtener o crear sesión (el backend siempre devuelve una)
      const session = await loadOrCreateSession();
      if (!session) {
        inFlightRef.current = false;
        setError("No se pudo obtener la sesión");
        setLoading(false);
        return;
      }
      sessionId = session.id;
    }

    // Obtener página aleatoria con sessionId
    pageApi.getRandomPageByTypes(
      sessionId,
      typesToSend,
      {}, // opciones vacías - sin exclusiones manuales
      (err, data) => {
        inFlightRef.current = false;
        if (err) {
          // Si es 404, significa que no hay más páginas disponibles
          if (err.status === 404) {
            alert("¡Felicidades! Has completado todas las páginas disponibles. Vuelve al selector de modos para comenzar una nueva partida.");
            navigate("/modo");
            return;
          }
          // Si es 403, la sesión no pertenece al usuario - recargar sesión
          if (err.status === 403) {
            setCurrentSession(null);
            alert("La sesión ha expirado. Se creará una nueva sesión.");
            fetchPage(); // Reintentar con nueva sesión
            return;
          }
          setError(err);
          setLoading(false);
          return;
        }

        setPage(sortPageQuestions(data));
        setLoading(false);
        setQuestionIndex(0);
      }
    );
  }, [mappedModality, pageApi, navigate, currentSession, loadOrCreateSession]);

  useEffect(() => {
    const initGame = async () => {
      const sessionType = getSessionType(mappedModality);

      // Si es un juego nuevo, cerrar la sesión actual primero
      if (isNewGame) {
        // Obtener sesión actual para cerrarla
        const activeSessionResult = await gameSessionService.getOrCreateSession(sessionType);
        if (activeSessionResult.success && activeSessionResult.data) {
          await gameSessionService.closeSession(activeSessionResult.data.id);
        }

        // Resetear score para juego nuevo
        const resetScore = await scoreService.resetScore();
        setScore(resetScore);
      } else {
        // Mantener score actual al continuar
        const currentScore = await scoreService.getScore();
        setScore(currentScore);
      }

      // Obtener o crear sesión (el backend siempre devuelve una sesión)
      const sessionResult = await gameSessionService.getOrCreateSession(sessionType);

      if (!sessionResult.success) {
        setError(sessionResult.error);
        setLoading(false);
        return;
      }

      setCurrentSession(sessionResult.data);

      // Si hay una página incompleta, cargarla directamente
      if (sessionResult.data.incompletePage) {
        setPage(sortPageQuestions(sessionResult.data.incompletePage));
        setQuestionIndex(0);
      }

      setLoading(false);
      // Si no hay página incompleta, fetchPage se ejecutará automáticamente por el useEffect
    };

    if (mappedModality && gameSessionService) {
      initGame();
    }
    // eslint-disable-next-line
  }, []);

  // Cargar primera página cuando se crea la sesión
  useEffect(() => {
    if (currentSession && !page && !loading) {
      fetchPage();
    }
    // eslint-disable-next-line
  }, [currentSession, page, loading]);

  useEffect(() => {
    if (!page) return;
    // Usar todas las preguntas sin filtrar por dificultad
    const questions = page.questions || [];
    // Solo fetch nueva página si no hay preguntas Y no estamos cargando
    if (questions.length === 0 && !loading) {
      fetchPage();
    }
    // eslint-disable-next-line
  }, [page?.id, loading]);

  const handleAnswer = async (answer) => {
    let newScore = score;
    let message = INCORRECT_MESSAGE;
    const isCorrect = answer.is_correct;

    // Registrar la respuesta del usuario en el servidor
    const allQuestions = page?.questions || [];
    const currentQuestion = allQuestions[questionIndex];
    if (currentQuestion && currentSession?.id && page?.id && answer?.id) {
      const result = await userAnswerService.recordUserAnswer(
        currentSession.id,
        page.id,
        currentQuestion.id,
        answer.id
      );
      if (!result.success) {
        console.warn("Error registrando respuesta del usuario:", result.error);
        // Continuar con la lógica normal aunque falle el registro
      }
    }

    if (isCorrect) {
      const pointsEarned = getRandomPoints();
      newScore = await scoreService.addScore(pointsEarned);
      setScore(newScore);
      message = getSuccessMessage(pointsEarned);
    }

    setFeedback({
      message,
      is_correct: isCorrect,
    });
    setShowModal(true);
  };

  const handleCloseModal = async () => {
    setShowModal(false);

    // Usar todas las preguntas sin filtrar por dificultad
    const allQuestions = page?.questions || [];

    if (questionIndex + 1 < allQuestions.length) {
      // Si hay más preguntas en la misma página, pasar a la siguiente
      setQuestionIndex((prev) => prev + 1);
    } else {
      // Se acabaron las preguntas de esta página
      // Marcar la página como completada
      await markCurrentPageAsCompleted();

      // Mostrar modal de soporte después de completar todas las preguntas de la página
      setShowSupportModal(true);
    }
  };

  const handleCloseSupportModal = () => {
    setShowSupportModal(false);
    // Cargar una nueva página
    fetchPage();
  };

  if (loading)
    return <div style={{ textAlign: "center", padding: "20px" }}>Cargando...</div>;

  if (error)
    return (
      <div style={{ textAlign: "center", color: "red", padding: "20px" }}>
        Error: {error.message || String(error)}
      </div>
    );

  if (!page)
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        No se encontró la página.
      </div>
    );

  // Usar todas las preguntas sin filtrar por dificultad
  const allQuestions = page.questions || [];
  const videoId = extractYouTubeId(page.video_link);
  const currentQuestion = allQuestions[questionIndex];

  return (
    <div
      style={{
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        padding: "40px",
        borderRadius: "10px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        position: "relative",
      }}
    >
      {/* Mostrar puntuación durante el juego */}
      <div style={{ position: "absolute", top: "20px", right: "40px", zIndex: 10 }}>
        <ScoreDisplay score={score} />
      </div>

      <div
        style={{
          padding: "20px",
          border: "1px solid #ddd",
          borderRadius: "8px",
          marginTop: "20px",
        }}
      >
        <p
          style={{
            textAlign: "center",
            fontSize: "1.2rem",
            lineHeight: "1.6",
            color: "#333",
            marginBottom: "40px",
          }}
          dangerouslySetInnerHTML={{
            __html: page.interpretation || page.body,
          }}
        />
      </div>

      {videoId && (
        <div
          style={{
            position: "relative",
            paddingBottom: "56.25%", // Ratio 16:9 para pantallas horizontales
            height: 0,
            overflow: "hidden",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            marginTop: "30px",
            maxWidth: "95vw", // Ocupa casi todo el ancho disponible
            margin: "30px auto",
          }}
          className="video-container-responsive"
        >
          <iframe
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
            src={`https://www.youtube.com/embed/${videoId}?controls=0&modestbranding=1&rel=0&iv_load_policy=3`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}

      {currentQuestion ? (
        <div style={{ textAlign: "center" }}>
          <h2 style={{ marginBottom: "50px", marginTop: "50px" }}>
            {currentQuestion.content}
          </h2>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
            }}
          >
             {currentQuestion.answers.map((answer, i) => (
               <button
                 onClick={() => handleAnswer(answer)}
                 key={i}
                 style={{
                   padding: "15px 30px",
                   fontSize: "1.2rem",
                   borderRadius: "8px",
                   border: "none",
                   cursor: "pointer",
                   background: BUTTON_COLORS[i % BUTTON_COLORS.length],
                   color: "#fff",
                   width: "100%",
                   maxWidth: "300px",
                   transition: "transform 0.2s",
                 }}
                 onMouseEnter={(e) => {
                   e.currentTarget.style.transform = "scale(1.05)";
                 }}
                 onMouseLeave={(e) => {
                   e.currentTarget.style.transform = "scale(1)";
                 }}
               >
                 {answer.content}
               </button>
             ))}
          </div>
        </div>
      ) : (
        <p style={{ textAlign: "center" }}>
          No hay preguntas disponibles para esta página.
        </p>
      )}

      {showModal && <FeedbackModal feedback={feedback} onClose={handleCloseModal} />}
      <SupportModal show={showSupportModal} onClose={handleCloseSupportModal} />
    </div>
  );
};

export default Game;
